import { useState } from 'react';
import { Search, X } from '../../lib/icons';
import { scoreBand, masteryColors, SCORE_COLORS } from './scoring';
import { cn } from '../../lib/cn';

const AVG_PILL = {
  low: 'bg-destructive/12 text-destructive',
  mid: 'bg-warning/15 text-warning-foreground',
  high: 'bg-success/12 text-success',
  none: 'bg-secondary text-muted-foreground',
};

function SpecificSkillRow({ skill, highlighted, onClick }) {
  const band = scoreBand(skill.class_avg);
  const mc = masteryColors(skill.mastery_pct);
  const graded = skill.n_graded > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full flex-col gap-1.5 rounded-lg border px-3 py-2 text-left transition-colors',
        highlighted ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary/40'
      )}
    >
      <div className="text-[13px] font-medium text-foreground">{skill.specific_skill}</div>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        {graded ? (
          <>
            <span className={cn('rounded-md px-1.5 py-0.5', AVG_PILL[band])}>
              Avg <strong>{skill.class_avg != null ? skill.class_avg.toFixed(1) : '—'}</strong>
            </span>
            <span
              className="rounded-md px-1.5 py-0.5"
              style={{ background: mc.bg, color: mc.fg }}
            >
              <strong>
                {skill.mastery_pct != null ? Math.round(skill.mastery_pct * 100) + '%' : '—'}
              </strong>{' '}
              mastery
            </span>
            <span className="text-muted-foreground">{skill.n_graded} graded</span>
          </>
        ) : (
          <span className="rounded-md bg-secondary px-1.5 py-0.5 text-muted-foreground">
            Not yet assessed
          </span>
        )}
      </div>
      {skill.n_graded > 0 && (
        <div className="flex h-4 overflow-hidden rounded">
          {[1, 2, 3, 4, 5].map((level) => {
            const count = skill.distribution[level] || 0;
            if (count === 0) return null;
            const pct = (count / skill.n_graded) * 100;
            return (
              <div
                key={level}
                className="flex items-center justify-center text-[9px] font-bold text-black/55"
                style={{ width: `${pct}%`, background: SCORE_COLORS[level] }}
                title={`Level ${level}: ${count} student${count === 1 ? '' : 's'} (${pct.toFixed(0)}%)`}
              >
                {pct >= 14 ? count : ''}
              </div>
            );
          })}
        </div>
      )}
    </button>
  );
}

export default function ClickedSkillPanel({ clickedNode, allSkills, onClear }) {
  const [highlightedId, setHighlightedId] = useState(null);

  const [prevNodeId, setPrevNodeId] = useState(clickedNode?.id);
  if (clickedNode?.id !== prevNodeId) {
    setPrevNodeId(clickedNode?.id);
    setHighlightedId(null);
  }

  if (!clickedNode) {
    return (
      <div className="ds flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border p-6 text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-secondary text-muted-foreground">
          <Search className="size-5" />
        </div>
        <h4 className="text-sm font-bold text-foreground">Drill into a skill</h4>
        <p className="text-[12.5px] leading-relaxed text-muted-foreground [&_strong]:font-semibold [&_strong]:text-foreground">
          <strong>Hover</strong> any arc to see its name, mastery, and class average.
          <br />
          <strong>Click</strong> a skill (outer ring) to see every skill developmental descriptor
          the project assesses under it.
        </p>
      </div>
    );
  }

  const specifics = allSkills.filter((s) => s.skill_id === clickedNode.id);
  const meta = clickedNode.classMeta || {};

  return (
    <div className="ds flex h-full flex-col rounded-xl border border-border bg-card">
      <div className="flex items-start justify-between gap-2 border-b border-border p-3">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Skill
          </span>
          <h4 className="text-sm font-bold text-foreground">{clickedNode.name}</h4>
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <span>{meta.area_name}</span>
            <span className="text-border">›</span>
            <span>{meta.competency_name}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {specifics.length === 0 ? (
        <div className="p-4 text-[13px] text-muted-foreground">
          No skill developmental descriptors are mapped to this skill in the project.
        </div>
      ) : (
        <>
          <div className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {specifics.length === 1
              ? '1 skill developmental descriptor under this skill'
              : `${specifics.length} skill developmental descriptors under this skill`}
          </div>
          <div className="flex flex-col gap-1.5 overflow-y-auto p-3">
            {/* Graded, worst-first, then ungraded last — same convention
                SkillsNeedingAttention uses on this same page. The previous
                `mastery_pct ?? -1` put every ungraded row ABOVE actual low
                scores (unassessed isn't the same as failing), which read as
                unsorted. */}
            {[...specifics]
              .sort((a, b) => {
                const aGraded = a.n_graded > 0;
                const bGraded = b.n_graded > 0;
                if (aGraded !== bGraded) return aGraded ? -1 : 1;
                if (aGraded && bGraded && a.mastery_pct !== b.mastery_pct) {
                  return (a.mastery_pct ?? 0) - (b.mastery_pct ?? 0);
                }
                return a.specific_skill.localeCompare(b.specific_skill);
              })
              .map((s) => (
                <SpecificSkillRow
                  key={s.specific_skill_id}
                  skill={s}
                  highlighted={s.specific_skill_id === highlightedId}
                  onClick={() =>
                    setHighlightedId((prev) =>
                      prev === s.specific_skill_id ? null : s.specific_skill_id
                    )
                  }
                />
              ))}
          </div>
        </>
      )}

      <div className="mt-auto border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        This panel is independent of the matrix below. Use the matrix&apos;s own search to filter
        rows.
      </div>
    </div>
  );
}
